import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Location } from "../types/Types";
import {GOOGLE_MAPS_API_KEY} from '../../config/googleMaps';

interface Coordinates {
  lat: number;
  lng: number;
}

interface Props {
  userCoordinates: Coordinates | undefined;
  location: Location | undefined;
  visible: boolean;
}

const mapContainerStyle = {
  width: "100%",
  height: "100%"
};

const mapOptions = {
  disableDefaultUI: true,
  gestureHandling: "cooperative",
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false
};

const NearestPubMap: React.FC<Props> = ({ userCoordinates, location, visible }) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY
  });

  const pubCoordinates = useMemo(() => {
    if (!location?.lat || !location?.lng) return undefined;
    return {
      lat: +location.lat,
      lng: +location.lng
    };
  }, [location]);

  const fitMapToMarkers = useCallback((mapToFit: google.maps.Map) => {
    if (!userCoordinates || !pubCoordinates) return;

    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(userCoordinates);
    bounds.extend(pubCoordinates);
    mapToFit.fitBounds(bounds, 48);

    if (userCoordinates.lat === pubCoordinates.lat && userCoordinates.lng === pubCoordinates.lng) {
      mapToFit.setZoom(15);
    }
  }, [pubCoordinates, userCoordinates]);

  useEffect(() => {
    if (!map || !visible) return;
    fitMapToMarkers(map);
  }, [fitMapToMarkers, map, visible]);

  const onLoad = useCallback((loadedMap: google.maps.Map) => {
    setMap(loadedMap);
    fitMapToMarkers(loadedMap);
  }, [fitMapToMarkers]);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  if (!isLoaded || !userCoordinates || !pubCoordinates) return null;

  return (
    <div className={`nearest-pub-map-reveal ${visible ? 'is-open' : ''}`}>
      <div className="nearest-pub-map">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={pubCoordinates}
          zoom={14}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={mapOptions}
        >
          <Marker
            position={userCoordinates}
            title="Your location"
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: "#2f80ed",
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 3
            }}
            zIndex={2}
          />
          <Marker
            position={pubCoordinates}
            title={location?.name || "Nearest pub"}
            icon={{
              url: "/images/new-marker.png",
              scaledSize: new window.google.maps.Size(30, 42)
            }}
            zIndex={1}
          />
        </GoogleMap>
      </div>
    </div>
  );
};

export default React.memo(NearestPubMap);
